// -----------------------------------------------------------------------------
// engine/player.ts — the playback clock + preview renderer.
//
// This is the engine's "own clock" (CLAUDE.md §4 principle 5). Rendering is
// ON-DEMAND: the preview is re-composited whenever something changes (an edit, a
// seek, a load, or a freshly-decoded frame arriving), and a continuous
// requestAnimationFrame loop runs ONLY while playing. That keeps an idle editor
// from burning GPU every frame and means the preview is correct even in a
// backgrounded tab (where rAF is throttled). The UI calls play/pause/seek and
// requestRender(); it never draws frames itself.
// -----------------------------------------------------------------------------
import { type ID, type MediaClip, clipEnd, projectDuration } from '../core/model';
import type { ProjectStore } from '../core/store';
import { Emitter } from '../shared/events';
import { clamp } from '../shared/time';
import { drawMediaLayer, drawTextLayer } from './compositor/layer';
import type { Compositor } from './compositor/gl';
import type { MediaManager } from './media/source';
import { compileRenderPlan } from './render-plan';

export interface PlayerEvents {
  time: number;
  playing: boolean;
}

export class Player {
  readonly events = new Emitter<PlayerEvents>();

  private rafId = 0;
  private playing = false;
  private currentTime = 0;
  private lastTs = 0;
  private mounted = false;

  constructor(
    private readonly store: ProjectStore,
    private readonly media: MediaManager,
    private readonly compositor: Compositor,
  ) {
    // A late-arriving decoded frame (async) should repaint the paused preview.
    this.media.onFrameReady = () => this.requestRender();
  }

  mount(): void {
    this.mounted = true;
    this.renderOnce(); // paint the initial frame
  }
  unmount(): void {
    this.mounted = false;
    this.stopLoop();
    this.playing = false;
    this.media.pauseAllAudio();
  }

  /** Repaint now if we're paused; during playback the loop already repaints. */
  requestRender(): void {
    if (!this.playing) this.renderOnce();
  }

  private renderOnce(): void {
    if (!this.mounted) return;
    this.renderAt(this.currentTime);
  }

  private stopLoop(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  private loop = (ts: number): void => {
    if (!this.playing) {
      this.rafId = 0;
      return;
    }
    // Clamp dt so a stalled/throttled rAF gap can't make the playhead leap.
    const dt = this.lastTs ? Math.min((ts - this.lastTs) / 1000, 0.1) : 0;
    this.lastTs = ts;

    const duration = projectDuration(this.store.state.project);
    this.currentTime += dt;
    if (this.currentTime >= duration) {
      this.currentTime = duration;
      this.playing = false;
      this.media.pauseAllAudio();
      this.events.emit('playing', false);
      this.events.emit('time', this.currentTime);
      this.renderOnce();
      this.rafId = 0;
      return;
    }
    this.events.emit('time', this.currentTime);
    this.renderAt(this.currentTime);
    this.syncAudio();
    this.rafId = requestAnimationFrame(this.loop);
  };

  // Keep the hidden <audio> elements in step with the playhead: start/seek the
  // active clips' sound, correct drift, and pause everything else.
  private syncAudio(): void {
    const state = this.store.state;
    const t = this.currentTime;
    const active = new Set<ID>();
    for (const track of state.project.tracks) {
      for (const clip of track.clips) {
        if (clip.kind === 'text') continue;
        if (t < clip.startSec || t >= clipEnd(clip)) continue;
        const asset = state.assets.find((a) => a.id === clip.assetId);
        if (!asset || !(asset.hasAudio || asset.kind === 'audio')) continue;
        const audio = this.media.getAudioElement(clip.assetId);
        if (!audio) continue;
        active.add(clip.assetId);
        const srcTime = Math.max(0, (clip as MediaClip).inSec + (t - clip.startSec));
        if (this.playing) {
          if (audio.paused) {
            audio.currentTime = srcTime;
            void audio.play().catch(() => {});
          } else if (Math.abs(audio.currentTime - srcTime) > 0.3) {
            audio.currentTime = srcTime;
          }
        } else {
          if (!audio.paused) audio.pause();
          if (Math.abs(audio.currentTime - srcTime) > 0.05) audio.currentTime = srcTime;
        }
      }
    }
    for (const [assetId, audio] of this.media.audioEntries()) {
      if (!active.has(assetId) && !audio.paused) audio.pause();
    }
  }

  // Composite a single frame at `time` into the preview canvas.
  private renderAt(time: number): void {
    const project = this.store.state.project;
    this.compositor.beginFrame(project.background);
    const textScale = this.compositor.height / project.height;
    const plan = compileRenderPlan(this.store.state, time);

    for (const layer of plan.layers) {
      if (layer.kind === 'media') {
        const source = this.media.getSource(layer.assetId);
        const bitmap = source?.peek(layer.sourceTimeSec);
        if (bitmap) drawMediaLayer(this.compositor, bitmap, layer.opacity, layer.adjust);
      } else {
        drawTextLayer(this.compositor, layer, textScale);
      }
    }
  }

  // ---- transport -------------------------------------------------------------
  play(): void {
    if (this.playing) return;
    if (this.currentTime >= projectDuration(this.store.state.project)) this.currentTime = 0;
    this.playing = true;
    this.lastTs = 0;
    this.events.emit('playing', true);
    this.syncAudio(); // start sound for the active clips (within the click gesture)
    if (!this.rafId) this.rafId = requestAnimationFrame(this.loop);
  }
  pause(): void {
    if (!this.playing) return;
    this.playing = false;
    this.stopLoop();
    this.media.pauseAllAudio();
    this.events.emit('playing', false);
    this.renderOnce();
  }
  toggle(): void {
    this.playing ? this.pause() : this.play();
  }
  seek(time: number): void {
    this.currentTime = clamp(time, 0, projectDuration(this.store.state.project));
    this.events.emit('time', this.currentTime);
    if (!this.playing) this.renderOnce();
    this.syncAudio(); // reposition (and resync if playing)
  }
  getTime(): number {
    return this.currentTime;
  }
  isPlaying(): boolean {
    return this.playing;
  }
}
